import type { ReactNode } from "react";
import { Clock, Repeat, Sparkles, Weight } from "lucide-react";
import {
  formatLoad,
  formatRest,
  formatSetsReps,
  hasStructuredFields,
  type ParsedWorkoutDetail,
} from "./workoutDetail";

type ChipTone = "default" | "info" | "muted" | "accent";

/**
 * Chip visual pequeno (prescrição / resumo). Tons mapeiam pras cores do tema
 * do portal. Portado do `Chip` de `ExercisePrescriptionView.tsx`.
 */
export function Chip({
  icon,
  label,
  tone = "default",
}: {
  icon: ReactNode;
  label: string;
  tone?: ChipTone;
}) {
  const toneClass =
    tone === "info"
      ? "border-border-strong bg-elevated text-ink"
      : tone === "muted"
        ? "border-border-subtle bg-elevated/60 text-muted"
        : tone === "accent"
          ? "border-accent-soft/50 bg-accent/15 text-accent-soft"
          : "border-border-subtle bg-elevated/80 text-ink";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium leading-none ${toneClass}`}
    >
      <span className="flex shrink-0 items-center text-muted">{icon}</span>
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
}

/**
 * Renderiza a prescrição parseada como chips. Se nada parseou, mostra o `raw`
 * como texto cinza (fallback). Portado de `ExercisePrescriptionView.tsx`.
 */
export function Prescription({ parsed }: { parsed: ParsedWorkoutDetail }) {
  if (!hasStructuredFields(parsed)) {
    if (!parsed.raw.trim()) return null;
    return (
      <p className="mt-1 whitespace-pre-line text-sm leading-relaxed text-muted">{parsed.raw}</p>
    );
  }

  const setsRepsLabel = formatSetsReps(parsed.sets, parsed.reps);
  const loadLabel = formatLoad(parsed.load_kg);
  const restLabel = formatRest(parsed.rest_seconds);

  return (
    <div className="animate-fade-in mt-1.5 flex flex-col gap-1.5">
      {(setsRepsLabel || loadLabel || restLabel) && (
        <div className="flex flex-wrap items-center gap-1.5">
          {setsRepsLabel && <Chip icon={<Repeat size={11} aria-hidden />} label={setsRepsLabel} />}
          {loadLabel && <Chip icon={<Weight size={11} aria-hidden />} label={loadLabel} tone="info" />}
          {restLabel && (
            <Chip icon={<Clock size={11} aria-hidden />} label={`${restLabel} descanso`} tone="muted" />
          )}
        </div>
      )}

      {parsed.technique && (
        <div className="flex items-start gap-1.5 text-xs leading-relaxed text-accent-soft">
          <Sparkles size={12} className="mt-0.5 shrink-0" aria-hidden />
          <span className="break-words">{parsed.technique}</span>
        </div>
      )}

      {parsed.notes && (
        <p className="text-xs leading-relaxed text-muted">
          <span className="text-faint">obs:</span> <span className="italic">{parsed.notes}</span>
        </p>
      )}
    </div>
  );
}
